export function buildRulesViewWpBlocksMap(ctx) {
	const rulesPreviewIframe = ctx.getRulesPreviewIframe();

	if (!rulesPreviewIframe || !rulesPreviewIframe.contentDocument) {
		return {
			ok: false,
			reason: 'rules_view_not_ready',
			blocksByType: {},
			totalBlocks: 0,
			pageUrl: ''
		};
	}

	const documentNode = rulesPreviewIframe.contentDocument;
	const targetDocuments = getHighlightTargetDocuments(ctx, documentNode, true);
	const blocksByType = {};
	let totalBlocks = 0;
	let excludedHeaderBlocks = 0;
	let excludedFooterBlocks = 0;
	const documentSummaries = [];

	for (const [documentIndex, targetDocument] of targetDocuments.entries()) {
		if (!targetDocument || !targetDocument.body) {
			continue;
		}

		const regionContext = createBlockRegionContext(ctx, targetDocument);
		const failedRuleKeysByBlockNode = collectFailedRuleKeysByBlockNode(ctx, targetDocument);
		documentSummaries.push(describeTargetDocument(ctx, targetDocument, documentIndex));
		const candidates = Array.from(targetDocument.querySelectorAll('[class*="wp-block-"], p'));

		for (const node of candidates) {
			if (!node || node.nodeType !== 1 || !node.classList) {
				continue;
			}

			const blockClassName = Array.from(node.classList).find((className) => className.startsWith('wp-block-'));
			const isParagraphTag = node.tagName.toLowerCase() === 'p';

			if (!blockClassName && !isParagraphTag) {
				continue;
			}

			const region = classifyBlockRegion(ctx, node, regionContext);

			if (region === 'header') {
				excludedHeaderBlocks += 1;
				continue;
			}

			if (region === 'footer') {
				excludedFooterBlocks += 1;
				continue;
			}

			const blockType = blockClassName
				? (blockClassName.replace(/^wp-block-/, '').trim() || 'unknown')
				: 'paragraph_tag';
			const blockEntry = {
				index: totalBlocks,
				documentIndex,
				tag: node.tagName.toLowerCase(),
				selector: buildNodeSelector(node),
				id: node.id || '',
				className: node.className || '',
				failedRuleKeys: Array.from(failedRuleKeysByBlockNode.get(node) || []),
				matchSource: blockClassName ? 'wp-block-class' : 'tag-fallback',
				textPreview: ctx.buildTextPreview(node.textContent || '', 120)
			};

			if (!Array.isArray(blocksByType[blockType])) {
				blocksByType[blockType] = [];
			}

			blocksByType[blockType].push(blockEntry);
			totalBlocks += 1;
		}
	}

	const result = {
		ok: true,
		reason: '',
		pageUrl: documentNode.location?.href || '',
		targetDocumentsCount: targetDocuments.length,
		documents: documentSummaries,
		totalBlocks,
		excludedHeaderBlocks,
		excludedFooterBlocks,
		blockTypesCount: Object.keys(blocksByType).length,
		blocksByType
	};

	return result;
}

function buildNodeSelector(node) {
	if (!node || node.nodeType !== 1) {
		return '';
	}

	const tag = String(node.tagName || 'div').toLowerCase();
	const id = String(node.id || '').trim();

	if (id) {
		return `${tag}#${escapeSelectorToken(id)}`;
	}

	const classTokens = String(node.className || '')
		.split(/\s+/)
		.map((token) => token.trim())
		.filter((token) => token.length > 0)
		.slice(0, 3)
		.map((token) => `.${escapeSelectorToken(token)}`)
		.join('');

	const position = resolveNthOfTypePosition(node);
	const nthSegment = position > 0 ? `:nth-of-type(${position})` : '';

	return `${tag}${classTokens}${nthSegment}`;
}

function resolveNthOfTypePosition(node) {
	if (!node?.parentElement) {
		return 1;
	}

	const sameTagSiblings = Array.from(node.parentElement.children).filter((child) => {
		return child?.tagName?.toLowerCase?.() === node.tagName.toLowerCase();
	});

	const index = sameTagSiblings.indexOf(node);
	return index >= 0 ? index + 1 : 1;
}

function escapeSelectorToken(value) {
	return String(value || '').replace(/([^a-zA-Z0-9_-])/g, '\\$1');
}

export function collectFailedRuleKeysByBlockNode(ctx, documentNode) {
	const result = new Map();

	if (!documentNode || !ctx.rulesViewSelectorsByRule || typeof ctx.rulesViewSelectorsByRule !== 'object') {
		return result;
	}

	const blockSelector = '[class*="wp-block-"], p';
	const addRuleKey = (blockNode, ruleKey) => {
		if (!blockNode || !ruleKey) {
			return;
		}

		if (!result.has(blockNode)) {
			result.set(blockNode, new Set());
		}

		result.get(blockNode).add(ruleKey);
	};

	for (const [ruleKey, selectors] of Object.entries(ctx.rulesViewSelectorsByRule)) {
		if (!Array.isArray(selectors) || selectors.length === 0) {
			continue;
		}

		for (const selector of selectors) {
			if (typeof selector !== 'string' || selector.length === 0) {
				continue;
			}

			let nodes = [];

			try {
				nodes = Array.from(documentNode.querySelectorAll(selector));
			} catch {
				nodes = [];
			}

			for (const node of nodes) {
				if (!node || node.nodeType !== 1) {
					continue;
				}

				const blockNode = node.matches?.(blockSelector)
					? node
					: node.closest?.(blockSelector);

				if (blockNode) {
					addRuleKey(blockNode, ruleKey);
				}
			}
		}
	}

	return result;
}

export function createBlockRegionContext(ctx, documentNode) {
	return {
		documentNode,
		headerContainers: collectBlockRegionContainers(ctx, documentNode, getBlockRegionHeaderSelectors(ctx)),
		footerContainers: collectBlockRegionContainers(ctx, documentNode, getBlockRegionFooterSelectors(ctx))
	};
}

export function getBlockRegionHeaderSelectors(_ctx) {
	return [
		'header',
		'[role="banner"]',
		'#header',
		'#masthead',
		'.site-header',
		'.main-header',
		'.masthead',
		'.header',
		'.wp-block-template-part[data-area="header"]'
	];
}

export function getBlockRegionFooterSelectors(_ctx) {
	return [
		'footer',
		'[role="contentinfo"]',
		'#footer',
		'#colophon',
		'.site-footer',
		'.main-footer',
		'.colophon',
		'.footer',
		'.wp-block-template-part[data-area="footer"]'
	];
}

export function collectBlockRegionContainers(_ctx, documentNode, selectors) {
	if (!documentNode || !Array.isArray(selectors) || selectors.length === 0) {
		return [];
	}

	const containersSet = new Set();

	for (const selector of selectors) {
		if (typeof selector !== 'string' || selector.length === 0) {
			continue;
		}

		let nodes = [];

		try {
			nodes = Array.from(documentNode.querySelectorAll(selector));
		} catch {
			nodes = [];
		}

		for (const node of nodes) {
			containersSet.add(node);
		}
	}

	return Array.from(containersSet);
}

export function classifyBlockRegion(ctx, node, regionContext) {
	if (!node || !regionContext) {
		return 'page';
	}

	if (isNodeWithinRegionContainers(ctx, node, regionContext.footerContainers || [])) {
		return 'footer';
	}

	if (isNodeWithinRegionContainers(ctx, node, regionContext.headerContainers || [])) {
		return 'header';
	}

	if (hasNodeAncestorToken(ctx, node, ['footer', 'colophon', 'site-footer', 'main-footer'])) {
		return 'footer';
	}

	if (hasNodeAncestorToken(ctx, node, ['header', 'masthead', 'site-header', 'main-header', 'topbar'])) {
		return 'header';
	}

	return 'page';
}

export function isNodeWithinRegionContainers(_ctx, node, containers) {
	if (!node || !Array.isArray(containers) || containers.length === 0) {
		return false;
	}

	for (const containerNode of containers) {
		if (containerNode?.contains?.(node)) {
			return true;
		}
	}

	return false;
}

export function hasNodeAncestorToken(_ctx, node, tokens) {
	if (!node || !Array.isArray(tokens) || tokens.length === 0) {
		return false;
	}

	let currentNode = node;
	let depth = 0;

	while (currentNode && depth < 8) {
		const idText = typeof currentNode.id === 'string' ? currentNode.id.toLowerCase() : '';
		const classText = typeof currentNode.className === 'string' ? currentNode.className.toLowerCase() : '';

		for (const token of tokens) {
			if (idText.includes(token) || classText.includes(token)) {
				return true;
			}
		}

		currentNode = currentNode.parentElement;
		depth += 1;
	}

	return false;
}

export function collectEditorCanvasDocuments(_ctx, rootDocument) {
	if (!rootDocument) {
		return [];
	}

	const canvasCandidates = Array.from(
		rootDocument.querySelectorAll(
			'iframe[name="editor-canvas"], .block-editor-iframe__container iframe, .edit-post-visual-editor iframe, .editor-styles-wrapper iframe'
		)
	);
	const editorDocuments = [];

	for (const iframeNode of canvasCandidates) {
		try {
			const canvasDocument = iframeNode?.contentDocument;

			if (!canvasDocument || !canvasDocument.body) {
				continue;
			}

			if (!editorDocuments.includes(canvasDocument)) {
				editorDocuments.push(canvasDocument);
			}
		} catch {
			// Ignore inaccessible iframe documents.
		}
	}

	return editorDocuments;
}

export function describeTargetDocument(_ctx, documentNode, index) {
	if (!documentNode) {
		return {
			index,
			href: '',
			title: '',
			bodyClass: ''
		};
	}

	return {
		index,
		href: documentNode.location?.href || '',
		title: documentNode.title || '',
		bodyClass: documentNode.body?.className || ''
	};
}

export function hasEditorCanvasIframe(_ctx, documentNode) {
	if (!documentNode) {
		return false;
	}

	return Boolean(
		documentNode.querySelector(
			'iframe[name="editor-canvas"], .edit-post-visual-editor iframe, .editor-styles-wrapper iframe, .block-editor-iframe__container iframe'
		)
	);
}

export function getHighlightTargetDocuments(ctx, rootDocument, includeNestedIframes = false) {
	if (!rootDocument) {
		return [];
	}

	// In WP editor mode, highlight only inside editor canvas documents.
	if (includeNestedIframes) {
		const editorDocuments = collectEditorCanvasDocuments(ctx, rootDocument);
		if (Array.isArray(editorDocuments) && editorDocuments.length > 0) {
			return editorDocuments;
		}
	}

	const documents = [rootDocument];

	if (!includeNestedIframes) {
		return documents;
	}

	const iframeNodes = Array.from(rootDocument.querySelectorAll('iframe'));

	for (const iframeNode of iframeNodes) {
		try {
			const iframeDocument = iframeNode.contentDocument;

			if (!iframeDocument || !iframeDocument.body) {
				continue;
			}

			if (!documents.includes(iframeDocument)) {
				documents.push(iframeDocument);
			}
		} catch {
			// Ignore cross-origin iframe documents.
		}
	}

	return documents;
}
